import React from 'react'
import Image from 'next/image'
import Link from 'next/link'
import { useAppContextSelector } from 'src/app-components/AppContext'

import { LeadsQuery, OpportunitiesQuery } from '@app/api/EntityQuery'
import { FilterQueryParams } from '@app/api/FilterQueryParams'
import { ContactResponse } from '@app/types'
import { routes } from '@app/utils/routes'

import { renderHotItem } from './renderListItem'
import { SalesActivityList } from './SalesActivityList'

// -----------------------------
// * HELPERS / CONSTANTS
// -----------------------------

function getHotLeadsQuery(userContact: ContactResponse) {
  const query = new LeadsQuery()
  const fq = new FilterQueryParams()

  const isHot = fq.textFilter({ field: 'quality.rating', operation: 'equals', value: 'Hot' })
  const isAssignedToMe = fq.textFilter({ field: 'primaryRep.id', operation: 'equals', value: userContact.id })

  // Appropriate for all sales activity lists
  const isNotQualified = fq.textFilter({ field: 'status.lookup', operation: 'notEqual', value: 'QUALIFIED' })
  const isNotInterested = fq.textFilter({ field: 'status.lookup', operation: 'notEqual', value: 'NOT_QUALIFIED' })
  // ----------------------------

  query.filter(fq.and(isHot, isAssignedToMe, isNotQualified, isNotInterested))

  return query
}

function getHotOppsQuery(userContact: ContactResponse) {
  const query = new OpportunitiesQuery()
  const fq = new FilterQueryParams()

  const isHot = fq.textFilter({ field: 'quality.rating', operation: 'equals', value: 'Hot' })

  const isAssignedToMe = fq.group(
    fq.or(
      fq.textFilter({ field: 'accountPortfolioManager.id', operation: 'equals', value: userContact.id }),
      fq.textFilter({ field: 'businessDevelopmentManager.id', operation: 'equals', value: userContact.id }),
      fq.textFilter({ field: 'insideSalesRep.id', operation: 'equals', value: userContact.id }),
      fq.setFilter({ field: 'estimators.id', values: [userContact.id] }),
    ),
  )

  // Appropriate for all sales activity lists
  const isNotWon = fq.textFilter({ field: 'stage', operation: 'notEqual', value: 'CLOSED_WON' })
  const isNotLost = fq.textFilter({ field: 'stage', operation: 'notEqual', value: 'CLOSED_LOST' })
  // ----------------------------

  query.filter(fq.and(isHot, isAssignedToMe, isNotWon, isNotLost))

  return query
}

const HOT_OPPS_URL = (function () {
  const searchParams = new URLSearchParams()

  const qualityRatingFilter = {
    filterType: 'set',
    type: 'equals',
    values: ['Hot'],
  }

  searchParams.set('view', 'my')
  searchParams.set('filterModel', btoa(JSON.stringify({ 'quality.rating': qualityRatingFilter })))

  return `${routes.opportunities}?${searchParams.toString()}`
})()

const HOT_LEADS_URL = (function () {
  const searchParams = new URLSearchParams()

  const qualityRatingFilter = {
    filterType: 'set',
    type: 'equals',
    values: ['Hot'],
  }

  searchParams.set('view', 'my')
  searchParams.set('filterModel', btoa(JSON.stringify({ 'quality.rating': qualityRatingFilter })))

  return `${routes.leads}?${searchParams.toString()}`
})()

// -----------------------------
// * COMPONENT
// -----------------------------

export function HotList() {
  const userContact = useAppContextSelector((state) => state.userContact)

  const leadsQuery = getHotLeadsQuery(userContact)
  const oppsQuery = getHotOppsQuery(userContact)

  const moreActions = (
    <div className="flex w-full flex-col gap-4xs py-xs text-[14px]">
      <Link href={HOT_LEADS_URL} className="px-[8px] py-[6px]">
        View Hot Leads
      </Link>
      <Link href={HOT_OPPS_URL} className="px-[8px] py-[6px]">
        View Hot Opportunities
      </Link>
    </div>
  )

  return (
    <SalesActivityList
      header="Hot Leads & Opportunities"
      moreActions={moreActions}
      emptyMessage="Nothing Here"
      emptyIcon={<Image alt="calendar icon" width={32} height={32} src="/fire.png" />}
      renderListItem={renderHotItem}
      leadsQuery={leadsQuery}
      oppsQuery={oppsQuery}
    />
  )
}
